import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { apiFetchBlob } from '../../lib/api'
import { ocrQuoteBoxes, quoteRanges, type OcrGeometry } from '../../lib/documentReader'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

export interface SourceAnnotation {
  id: string
  document_id: string
  object_type: string
  object_id: string | null
  proposal_id: string | null
  field: string
  value: unknown
  proposal_value?: unknown
  proposal_value_changed?: boolean
  source?: { entity_name?: string; value?: unknown } | null
  page: number | null
  quote: string | null
  origin: 'document_reference' | 'evidence' | 'proposal'
  status: string
  location_status: 'captured' | 'page_only' | 'no_location' | 'invalid_page'
}
interface Mark {
  id: string
  left: number
  top: number
  width: number
  height: number
}

function SourcePage({
  page,
  width,
  annotations,
  selected,
  onSelect,
  ocr,
  onNeedsOcr,
}: {
  ocr?: OcrGeometry | null
  onNeedsOcr?: () => void
  page: number
  width: number
  annotations: SourceAnnotation[]
  selected: string | null
  onSelect: (id: string) => void
}) {
  const host = useRef<HTMLDivElement>(null)
  const [marks, setMarks] = useState<Mark[]>([])
  const [unlocated, setUnlocated] = useState<string[]>([])
  const [textReady, setTextReady] = useState(0)
  useEffect(() => {
    const node = host.current
    const layer = node?.querySelector('.react-pdf__Page__textContent')
    if (!node || !textReady) return
    const walker = layer ? document.createTreeWalker(layer, NodeFilter.SHOW_TEXT) : null
    const pieces: { node: Node; start: number; end: number }[] = []
    let text = ''
    let next: Node | null
    while (walker && (next = walker.nextNode())) {
      const value = next.textContent ?? ''
      pieces.push({ node: next, start: text.length, end: text.length + value.length })
      text += value
    }
    const box = node.getBoundingClientRect()
    const found: Mark[] = []
    const missing: string[] = []
    for (const annotation of annotations) {
      if (!annotation.quote || annotation.location_status !== 'captured') continue
      const matches = quoteRanges(text, annotation.quote)
      if (!matches.length) {
        const words = ocr ? ocrQuoteBoxes(ocr, annotation.quote) : []
        if (words.length && ocr) {
          for (const word of words)
            found.push({
              id: annotation.id,
              left: (word.left / ocr.render_width) * box.width,
              top: (word.top / ocr.render_height) * box.height,
              width: (word.width / ocr.render_width) * box.width,
              height: (word.height / ocr.render_height) * box.height,
            })
        } else missing.push(annotation.id)
      }
      for (const match of matches) {
        const start = pieces.find((p) => p.start <= match.start && p.end > match.start)
        const end = pieces.find((p) => p.start < match.end && p.end >= match.end)
        if (!start || !end) continue
        const range = document.createRange()
        range.setStart(start.node, match.start - start.start)
        range.setEnd(end.node, match.end - end.start)
        for (const rect of Array.from(range.getClientRects())) {
          if (rect.width > 0 && rect.height > 0)
            found.push({
              id: annotation.id,
              left: rect.left - box.left,
              top: rect.top - box.top,
              width: rect.width,
              height: rect.height,
            })
        }
      }
    }
    setMarks(found)
    setUnlocated(missing)
    if (missing.length && !ocr) onNeedsOcr?.()
  }, [textReady, annotations, width, ocr, onNeedsOcr])
  const textRendered = useCallback(() => setTextReady((n) => n + 1), [])
  useEffect(() => {
    if (!selected) return
    const marked = host.current?.querySelector(`[data-source="${CSS.escape(selected)}"]`)
    marked?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
  }, [selected, marks])
  const selectedMissing = selected && unlocated.includes(selected)
  return (
    <div className="document-source-page" data-testid="reader-source-page" data-page={page}>
      {selectedMissing ? (
        <p className="reader-note" role="status">
          The saved passage could not be located on this page. Read the original; no highlight has
          been guessed.
        </p>
      ) : null}
      <div className="document-page-canvas" ref={host}>
        <Page
          pageNumber={page}
          width={width}
          renderTextLayer
          renderAnnotationLayer
          onRenderTextLayerSuccess={textRendered}
          onRenderTextLayerError={textRendered}
          onGetTextError={textRendered}
        />
        <div className="document-highlight-layer" aria-hidden="true">
          {marks.map((mark, i) => (
            <span
              key={`${mark.id}:${i}`}
              data-source={mark.id}
              className={`document-highlight${selected === mark.id ? ' selected' : ''}`}
              style={{ left: mark.left, top: mark.top, width: mark.width, height: mark.height }}
              onClick={() => onSelect(mark.id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default function DocumentPdf({
  documentId,
  session,
  page,
  zoom,
  annotations,
  selected,
  onSelect,
  onPages,
  ocr,
  onNeedsOcr,
}: {
  ocr?: OcrGeometry | null
  onNeedsOcr?: () => void
  documentId: string
  session: string | null
  page: number
  zoom: number
  annotations: SourceAnnotation[]
  selected: string | null
  onSelect: (id: string) => void
  onPages: (count: number) => void
}) {
  const [fileState, setFileState] = useState<{ key: string; bytes: ArrayBuffer } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pages, setPages] = useState(0)
  const [width, setWidth] = useState(720)
  const container = useRef<HTMLDivElement>(null)
  const key = `${documentId}:${session ?? ''}`
  useEffect(() => {
    const controller = new AbortController()
    setFileState(null)
    setError(null)
    setPages(0)
    apiFetchBlob(`/api/documents/${encodeURIComponent(documentId)}/content`, {
      signal: controller.signal,
    })
      .then((blob) => blob.arrayBuffer())
      .then((bytes) => {
        if (!controller.signal.aborted) setFileState({ key, bytes })
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : 'Could not load the original PDF')
      })
    return () => controller.abort()
  }, [documentId, key])
  useEffect(() => {
    const node = container.current
    if (!node) return
    const observer = new ResizeObserver((entries) =>
      setWidth(Math.max(280, entries[0].contentRect.width - 32))
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const bytes = fileState?.key === key ? fileState.bytes : null
  const file = useMemo(() => (bytes ? { data: new Uint8Array(bytes.slice(0)) } : null), [bytes])
  const scoped = useMemo(() => annotations.filter((a) => a.page === page), [annotations, page])
  const loaded = useCallback(
    ({ numPages }: { numPages: number }) => {
      setPages(numPages)
      onPages(numPages)
    },
    [onPages]
  )
  return (
    <div className="document-pdf-scroll" ref={container}>
      {error ? (
        <p className="reader-error" role="alert">
          {error}
        </p>
      ) : !file ? (
        <p className="reader-note" role="status">
          Loading original PDF…
        </p>
      ) : (
        <Document
          key={key}
          file={file}
          onLoadSuccess={loaded}
          onLoadError={() =>
            setError('Could not open this PDF. A password-protected file needs an unlocked copy.')
          }
          loading={<p className="reader-note">Reading PDF…</p>}
        >
          {pages && page <= pages ? (
            <SourcePage
              key={`${documentId}:${page}`}
              page={page}
              width={Math.max(200, width * zoom)}
              annotations={scoped}
              selected={selected}
              onSelect={onSelect}
              ocr={ocr}
              onNeedsOcr={onNeedsOcr}
            />
          ) : pages ? (
            <p className="reader-error">
              Page {page} does not exist in this {pages}-page document.
            </p>
          ) : null}
        </Document>
      )}
    </div>
  )
}
